import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ciRoleCan } from "../../shared/permissions";
import { type MockRoute } from "../../shared/types";
import { Badge, methodTone, statusTone } from "../components/Badge";
import { Button } from "../components/Button";
import { CopyButton } from "../components/CopyButton";
import { EmptyState, ErrorBanner, Loading } from "../components/Feedback";
import {
  deleteMock,
  deleteRoute,
  fetchConfig,
  fetchMock,
  mockKeys,
  mockUrl,
  updateMock,
} from "../features/mocks/mock-queries";
import { RequestsPanel } from "../features/mocks/RequestsPanel";
import { RouteFormModal } from "../features/mocks/RouteFormModal";
import { type CiSession } from "../lib/auth-client";
import { formatDateTime, formatRelative, isExpired } from "../lib/format";
import { Link, useRouter } from "../lib/router";

type MockDetailPageProps = {
  /** Signed-in session */
  session: CiSession;
  /** Mock token from the URL */
  tokenUuid: string;
};

const EXTEND_DAYS = 30;

/** One mock: its URL, routes and the requests it received. */
export const MockDetailPage = ({ session, tokenUuid }: MockDetailPageProps) => {
  const queryClient = useQueryClient();
  const { navigate } = useRouter();
  const config = useQuery({ queryKey: ["config"], queryFn: fetchConfig });
  const mock = useQuery({
    queryKey: mockKeys.detail(tokenUuid),
    queryFn: () => fetchMock(tokenUuid),
  });
  const [editing, setEditing] = useState<MockRoute | "new" | null>(null);
  const [renaming, setRenaming] = useState(false);
  const canWrite = ciRoleCan({
    role: session.user.role,
    permissions: { mock: ["write"] },
  });
  const canDelete = ciRoleCan({
    role: session.user.role,
    permissions: { mock: ["delete"] },
  });

  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: mockKeys.detail(tokenUuid),
    });
    void queryClient.invalidateQueries({ queryKey: mockKeys.all });
  };
  const update = useMutation({
    mutationFn: (body: Parameters<typeof updateMock>[1]) =>
      updateMock(tokenUuid, body),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => deleteMock(tokenUuid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mockKeys.all });
      navigate("/mocks");
    },
  });
  const removeRoute = useMutation({
    mutationFn: (routeUuid: string) => deleteRoute(tokenUuid, routeUuid),
    onSuccess: refresh,
  });

  if (mock.isLoading) {
    return <Loading />;
  }
  if (!mock.data) {
    return (
      <div className="space-y-4">
        <Link to="/mocks" className="text-sm text-sky-700 hover:underline">
          ← API mocks
        </Link>
        <ErrorBanner error={mock.error ?? new Error("Mock not found")} />
      </div>
    );
  }
  const data = mock.data;
  const baseUrl = mockUrl(config.data, data.uuid);
  const expired = isExpired(data.expiresAt);

  return (
    <div className="space-y-6">
      <Link to="/mocks" className="text-sm text-sky-700 hover:underline">
        ← API mocks
      </Link>
      <ErrorBanner error={update.error ?? remove.error ?? removeRoute.error} />

      <section className="card space-y-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {renaming ? (
              <form
                className="flex gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  const label = String(
                    new FormData(event.currentTarget).get("label"),
                  ).trim();
                  update.mutate(
                    { label: label || null },
                    { onSuccess: () => setRenaming(false) },
                  );
                }}
              >
                <input
                  name="label"
                  className="input"
                  defaultValue={data.label ?? ""}
                  autoFocus
                  maxLength={200}
                />
                <Button type="submit" size="sm">
                  Save
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setRenaming(false)}
                >
                  Cancel
                </Button>
              </form>
            ) : (
              <h1 className="flex items-center gap-2 text-xl font-semibold">
                {data.label || "Untitled mock"}
                {canWrite ? (
                  <button
                    type="button"
                    className="text-sm font-normal text-sky-700 hover:underline"
                    onClick={() => setRenaming(true)}
                  >
                    Rename
                  </button>
                ) : null}
              </h1>
            )}
            <p className="mt-1 text-sm text-slate-500">
              Created {formatRelative(data.createdAt)} by{" "}
              {data.createdBy
                ? data.createdBy.name || data.createdBy.email
                : `CI${data.ci.testSuite ? ` (${data.ci.testSuite})` : ""}`}
              {data.ci.testName ? ` · ${data.ci.testName}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {expired ? <Badge tone="rose">expired</Badge> : null}
            <span
              className="text-sm text-slate-600"
              title={formatDateTime(data.expiresAt)}
            >
              {data.expiresAt
                ? `${expired ? "Expired" : "Expires"} ${formatRelative(data.expiresAt)}`
                : "Never expires"}
            </span>
            {canWrite ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => update.mutate({ expiresInDays: EXTEND_DAYS })}
                disabled={update.isLoading}
              >
                Extend {EXTEND_DAYS} days
              </Button>
            ) : null}
            {canDelete ? (
              <Button
                variant="danger"
                size="sm"
                disabled={remove.isLoading}
                onClick={() =>
                  window.confirm(
                    "Delete this mock, its routes and every recorded request?",
                  ) && remove.mutate()
                }
              >
                Delete
              </Button>
            ) : null}
          </div>
        </div>
        <div>
          <p className="label">Base URL</p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm">
              {baseUrl}
            </code>
            <CopyButton value={baseUrl} />
          </div>
          {!config.data?.mockBaseUrl ? (
            <p className="mt-1 text-xs text-amber-700">
              CI_MOCK_PUBLIC_URL is not set on the API, so only the path is
              shown.
            </p>
          ) : null}
        </div>
      </section>

      <section className="card">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="font-semibold">Routes</h2>
          {canWrite ? (
            <Button size="sm" onClick={() => setEditing("new")}>
              Add route
            </Button>
          ) : null}
        </div>
        {!data.routes.length ? (
          <EmptyState title="No routes yet">
            Every request answers 404 until you add one. Requests are still
            recorded.
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="table-head">Request</th>
                  <th className="table-head">Response</th>
                  <th className="table-head">URL</th>
                  <th className="table-head" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.routes.map((route) => {
                  const url = mockUrl(
                    config.data,
                    data.uuid,
                    route.requestPath,
                  );
                  return (
                    <tr key={route.uuid}>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <Badge tone={methodTone(route.requestMethod)}>
                            {route.requestMethod === "*"
                              ? "ANY"
                              : route.requestMethod}
                          </Badge>
                          <code className="font-mono text-xs">
                            {route.requestPath}
                            {route.matchType === "prefix" ? (
                              <span className="text-slate-400">{"/**"}</span>
                            ) : null}
                          </code>
                        </div>
                        {route.description ? (
                          <p className="mt-1 text-xs text-slate-500">
                            {route.description}
                          </p>
                        ) : null}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <Badge tone={statusTone(route.responseStatus)}>
                            {route.responseStatus}
                          </Badge>
                          <span className="font-mono text-xs text-slate-500">
                            {route.responseType}
                          </span>
                          {route.delayMs ? (
                            <span className="text-xs text-slate-500">
                              +{route.delayMs} ms
                            </span>
                          ) : null}
                        </div>
                        {route.responseContent ? (
                          <p className="mt-1 max-w-md truncate font-mono text-xs text-slate-400">
                            {route.responseContent}
                          </p>
                        ) : null}
                      </td>
                      <td className="table-cell">
                        <CopyButton value={url} label="Copy URL" />
                      </td>
                      <td className="table-cell whitespace-nowrap text-right">
                        {canWrite ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditing(route)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                window.confirm(
                                  `Delete ${route.requestMethod} ${route.requestPath}?`,
                                ) && removeRoute.mutate(route.uuid)
                              }
                            >
                              Delete
                            </Button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <RequestsPanel tokenUuid={data.uuid} canClear={canWrite} />

      {editing ? (
        <RouteFormModal
          tokenUuid={data.uuid}
          route={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
};
